import { useState, useEffect } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Clock, User, Tag, ArrowRight, Mail, CheckCircle, Search, ChevronLeft, ChevronRight, MapPin, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, { email });
      setIsSubscribed(true);
      toast.success(res.data.message);
    } catch { toast.error("Erreur lors de l'inscription."); }
    finally { setIsSubmitting(false); }
  };

  if (isSubscribed) {
    return (
      <div className="flex items-center gap-3 bg-[#00FF66]/10 border border-[#00FF66]/30 px-6 py-4" data-testid="newsletter-success">
        <CheckCircle className="w-6 h-6 text-[#00FF66] shrink-0" />
        <p className="text-white text-sm font-medium">Inscrit(e) ! Vérifiez votre boîte mail.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 w-full md:w-auto" data-testid="newsletter-form">
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="votre@email.com" required
        className="bg-[#151515] border border-[#1a1a1a] text-white px-5 py-3 text-sm focus:outline-none focus:border-[#00FF66]/50 placeholder-[#666] min-w-[260px]" data-testid="newsletter-email-input" />
      <Button type="submit" disabled={isSubmitting}
        className="bg-[#00FF66] text-black hover:bg-[#00dd55] font-bold px-6 py-3 rounded-none whitespace-nowrap disabled:opacity-50" data-testid="newsletter-submit-btn">
        {isSubmitting ? "..." : "S'inscrire"}
      </Button>
    </form>
  );
}

function BlogList() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState({ articles: [], total: 0, pages: 0, categories: [], departments: [] });
  const [loading, setLoading] = useState(true);

  const page = parseInt(searchParams.get("page") || "1");
  const category = searchParams.get("cat") || "";
  const dept = searchParams.get("dept") || "";
  const search = searchParams.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 24 });
    if (category) params.set("category", category);
    if (dept) params.set("city", dept);
    if (search) params.set("search", search);

    axios.get(`${API}/blog/articles?${params}`)
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page, category, dept, search]);

  const setPage = (p) => { const sp = new URLSearchParams(searchParams); sp.set("page", p); setSearchParams(sp); window.scrollTo(0, 0); };
  const setCategory = (c) => { const sp = new URLSearchParams(searchParams); if (c) sp.set("cat", c); else sp.delete("cat"); sp.set("page", "1"); setSearchParams(sp); };
  const setDept = (d) => { const sp = new URLSearchParams(searchParams); if (d) sp.set("dept", d); else sp.delete("dept"); sp.set("page", "1"); setSearchParams(sp); };
  const doSearch = (e) => { e.preventDefault(); const sp = new URLSearchParams(searchParams); if (searchInput) sp.set("q", searchInput); else sp.delete("q"); sp.set("page", "1"); setSearchParams(sp); };

  const DEPT_LABELS = { "93": "Seine-Saint-Denis (93)", "92": "Hauts-de-Seine (92)", "94": "Val-de-Marne (94)", "75": "Paris (75)", "77": "Seine-et-Marne (77)", "78": "Yvelines (78)", "91": "Essonne (91)", "95": "Val-d'Oise (95)" };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-['Outfit',sans-serif]">
      <div className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-4 py-5 flex items-center gap-4">
          <Link to="/" className="text-[#00FF66] hover:text-[#00FF66]/80 transition-colors" data-testid="blog-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-[#00FF66]" />
            <span className="font-['Clash_Display',sans-serif] text-lg font-bold text-white">PUB PRO</span>
          </div>
          <span className="text-zinc-500 text-sm">/ Blog</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <p className="text-[#00FF66] uppercase tracking-[0.3em] text-xs font-bold mb-3">BLOG & CONSEILS ENSEIGNES</p>
          <h1 className="font-['Clash_Display',sans-serif] text-3xl sm:text-4xl font-bold tracking-tight" data-testid="blog-title">
            {data.total} articles pour votre projet d'enseigne
          </h1>
        </div>

        {/* Search */}
        <form onSubmit={doSearch} className="flex gap-2 mb-6">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Rechercher une ville, un type d'enseigne..."
              className="w-full bg-[#0a0a0a] border border-[#1a1a1a] text-white pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:border-[#00FF66]/30 placeholder-zinc-600"
              data-testid="blog-search-input" />
          </div>
          <button type="submit" className="bg-[#00FF66] text-black px-5 py-2.5 font-bold text-sm uppercase tracking-wider" data-testid="blog-search-btn">
            Rechercher
          </button>
          {search && <button type="button" onClick={() => { setSearchInput(""); const sp = new URLSearchParams(searchParams); sp.delete("q"); sp.set("page", "1"); setSearchParams(sp); }}
            className="border border-[#333] text-zinc-400 px-4 py-2.5 text-sm hover:text-white transition-colors">Effacer</button>}
        </form>

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-8">
          {/* Department filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <MapPin className="w-4 h-4 text-zinc-500" />
            <button onClick={() => setDept("")}
              className={`px-3 py-1 text-xs font-semibold transition-all ${!dept ? "bg-[#00FF66] text-black" : "bg-[#0a0a0a] border border-[#1a1a1a] text-zinc-400 hover:text-white"}`}>
              Tous
            </button>
            {(data.departments || []).sort().map(d => (
              <button key={d} onClick={() => setDept(d)}
                className={`px-3 py-1 text-xs font-semibold transition-all ${dept === d ? "bg-[#00FF66] text-black" : "bg-[#0a0a0a] border border-[#1a1a1a] text-zinc-400 hover:text-white"}`}
                data-testid={`dept-filter-${d}`}>
                {d === "93" ? "93 ★" : d}
              </button>
            ))}
          </div>

          {/* Category filter */}
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-4 h-4 text-zinc-500" />
            <button onClick={() => setCategory("")}
              className={`px-3 py-1 text-xs font-semibold transition-all ${!category ? "bg-[#00FF66] text-black" : "bg-[#0a0a0a] border border-[#1a1a1a] text-zinc-400 hover:text-white"}`}>
              Tous types
            </button>
            {(data.categories || []).slice(0, 8).map(c => (
              <button key={c} onClick={() => setCategory(c)}
                className={`px-3 py-1 text-xs font-semibold transition-all ${category === c ? "bg-[#00FF66] text-black" : "bg-[#0a0a0a] border border-[#1a1a1a] text-zinc-400 hover:text-white"}`}
                data-testid={`cat-filter-${c}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Articles Grid */}
        {loading ? (
          <div className="text-center text-zinc-500 py-20">Chargement...</div>
        ) : data.articles.length === 0 ? (
          <div className="text-center text-zinc-500 py-20">Aucun article trouvé</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {data.articles.map((article) => (
              <Link key={article.id} to={`/blog/${article.slug}`}
                className="group bg-[#0a0a0a] border border-[#1a1a1a] overflow-hidden hover:border-[#00FF66]/30 transition-all duration-300"
                data-testid={`blog-card-${article.id}`}>
                <div className="aspect-video overflow-hidden">
                  <img src={article.image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="bg-[#00FF66]/10 text-[#00FF66] text-[10px] font-bold px-2 py-0.5">{article.category}</span>
                    {article.city && <span className="text-zinc-600 text-[10px] flex items-center gap-0.5"><MapPin className="w-3 h-3" />{article.city}</span>}
                    <span className="text-zinc-600 text-[10px] flex items-center gap-0.5"><Clock className="w-3 h-3" />{article.read_time}</span>
                  </div>
                  <h2 className="font-bold text-sm mb-1.5 group-hover:text-[#00FF66] transition-colors line-clamp-2 leading-snug">{article.title}</h2>
                  <p className="text-zinc-500 text-xs line-clamp-2">{article.excerpt}</p>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {data.pages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-10" data-testid="blog-pagination">
            <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
              className="w-10 h-10 flex items-center justify-center bg-[#0a0a0a] border border-[#1a1a1a] text-zinc-400 hover:text-white disabled:opacity-30 transition-all">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(data.pages, 10) }, (_, i) => {
              let p;
              if (data.pages <= 10) p = i + 1;
              else if (page <= 5) p = i + 1;
              else if (page >= data.pages - 4) p = data.pages - 9 + i;
              else p = page - 4 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-10 h-10 flex items-center justify-center text-sm font-bold transition-all ${
                    p === page ? "bg-[#00FF66] text-black" : "bg-[#0a0a0a] border border-[#1a1a1a] text-zinc-400 hover:text-white"
                  }`} data-testid={`page-${p}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(Math.min(data.pages, page + 1))} disabled={page >= data.pages}
              className="w-10 h-10 flex items-center justify-center bg-[#0a0a0a] border border-[#1a1a1a] text-zinc-400 hover:text-white disabled:opacity-30 transition-all">
              <ChevronRight className="w-4 h-4" />
            </button>
            <span className="ml-4 text-xs text-zinc-600">Page {page}/{data.pages} — {data.total} articles</span>
          </div>
        )}

        {/* Newsletter */}
        <div className="mt-12 bg-[#00FF66]/5 border border-[#00FF66]/20 p-8 md:p-10" data-testid="newsletter-section">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Mail className="w-5 h-5 text-[#00FF66]" />
                <span className="text-[#00FF66] text-sm font-bold uppercase tracking-wider">Newsletter</span>
              </div>
              <h2 className="text-xl font-bold text-white mb-2 font-['Clash_Display',sans-serif]">Recevez nos conseils enseigne</h2>
              <p className="text-zinc-400 text-sm">Astuces, tendances et offres exclusives. Gratuit et sans spam.</p>
            </div>
            <NewsletterForm />
          </div>
        </div>

        <div className="mt-8 text-center bg-[#0a0a0a] border border-[#1a1a1a] p-8">
          <h2 className="text-xl font-bold mb-3 font-['Clash_Display',sans-serif]">Prêt à transformer votre façade ?</h2>
          <p className="text-zinc-400 mb-6 text-sm">Devis gratuit + 500€ HT de remise sur votre enseigne</p>
          <Link to="/">
            <Button className="bg-[#00FF66] text-black hover:bg-[#00dd55] font-bold px-8 py-3 rounded-none" data-testid="blog-cta-btn">
              Demander mon devis gratuit
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}

function BlogArticle() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/articles/${slug}`)
      .then(res => { setArticle(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center font-['Outfit',sans-serif]">Chargement...</div>;
  if (!article) return <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center font-['Outfit',sans-serif]">Article non trouvé</div>;

  return (
    <div className="min-h-screen bg-[#050505] text-white font-['Outfit',sans-serif]">
      <div className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
        <div className="max-w-4xl mx-auto px-4 py-5 flex items-center gap-4">
          <Link to="/blog" className="text-[#00FF66] hover:text-[#00FF66]/80 transition-colors" data-testid="article-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-[#00FF66]" />
            <Link to="/" className="font-['Clash_Display',sans-serif] text-lg font-bold text-white">PUB PRO</Link>
          </div>
          <span className="text-zinc-500 text-sm">/ Blog</span>
        </div>
      </div>

      <article className="max-w-4xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-5 text-xs text-zinc-400 flex-wrap">
          <span className="bg-[#00FF66]/10 text-[#00FF66] font-bold px-3 py-1 flex items-center gap-1">
            <Tag className="w-3 h-3" /> {article.category}
          </span>
          {article.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {article.city}</span>}
          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.read_time}</span>
          <span className="flex items-center gap-1"><User className="w-3 h-3" /> {article.author}</span>
          <span>{article.date}</span>
        </div>

        <h1 className="font-['Clash_Display',sans-serif] text-2xl sm:text-3xl font-bold mb-6 tracking-tight" data-testid="article-title">{article.title}</h1>

        <div className="overflow-hidden mb-8 aspect-video">
          <img src={article.image} alt={article.title} className="w-full h-full object-cover" />
        </div>

        <div
          className="prose prose-invert max-w-none
            [&_h2]:font-['Clash_Display',sans-serif] [&_h2]:text-xl [&_h2]:font-bold [&_h2]:text-white [&_h2]:mt-10 [&_h2]:mb-4
            [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-[#00FF66] [&_h3]:mt-6 [&_h3]:mb-3
            [&_p]:text-zinc-400 [&_p]:leading-relaxed [&_p]:mb-4 [&_p]:text-sm
            [&_ul]:space-y-2 [&_ul]:ml-4 [&_li]:text-zinc-400 [&_li]:text-sm
            [&_strong]:text-white
            [&_table]:w-full [&_table]:border-collapse [&_table]:my-6
            [&_th]:bg-[#0a0a0a] [&_th]:text-[#00FF66] [&_th]:text-left [&_th]:p-3 [&_th]:border [&_th]:border-[#1a1a1a] [&_th]:text-xs [&_th]:font-bold [&_th]:uppercase [&_th]:tracking-wider
            [&_td]:p-3 [&_td]:border [&_td]:border-[#1a1a1a] [&_td]:text-zinc-400 [&_td]:text-sm"
          dangerouslySetInnerHTML={{ __html: article.content }}
          data-testid="article-content"
        />

        <div className="mt-12 bg-[#00FF66]/5 border border-[#00FF66]/20 p-8 text-center">
          <h2 className="text-xl font-bold mb-3 font-['Clash_Display',sans-serif]">Envie de passer à l'action ?</h2>
          <p className="text-zinc-400 mb-6 text-sm">Devis gratuit + 500€ HT de remise sur votre enseigne</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/"><Button className="bg-[#00FF66] text-black hover:bg-[#00dd55] font-bold px-8 py-3 rounded-none" data-testid="article-cta-form">Demander mon devis</Button></Link>
            <a href="https://wa.me/33643693688" target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] text-white hover:bg-[#20bd5a] font-bold px-8 py-3 rounded-none" data-testid="article-cta-whatsapp">WhatsApp</Button>
            </a>
          </div>
        </div>

        {/* Maillage interne — Articles similaires */}
        {article.related && article.related.length > 0 && (
          <div className="mt-12" data-testid="related-articles">
            <div className="mb-6">
              <p className="text-[#00FF66] uppercase tracking-[0.3em] text-xs font-bold mb-2">ARTICLES SIMILAIRES</p>
              <h2 className="font-['Clash_Display',sans-serif] text-xl font-bold">Ces articles pourraient aussi vous intéresser</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {article.related.map((rel) => (
                <Link key={rel.slug} to={`/blog/${rel.slug}`}
                  className="group bg-[#0a0a0a] border border-[#1a1a1a] overflow-hidden hover:border-[#00FF66]/30 transition-all"
                  data-testid={`related-${rel.slug}`}>
                  <div className="aspect-video overflow-hidden">
                    <img src={rel.image} alt={rel.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="bg-[#00FF66]/10 text-[#00FF66] text-[9px] font-bold px-1.5 py-0.5">{rel.category}</span>
                      {rel.city && <span className="text-zinc-600 text-[9px] flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5" />{rel.city}</span>}
                    </div>
                    <h3 className="font-bold text-xs group-hover:text-[#00FF66] transition-colors line-clamp-2 leading-snug">{rel.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="mt-8">
          <Link to="/blog" className="text-[#00FF66] hover:underline font-medium flex items-center gap-2 text-sm" data-testid="article-back-link">
            <ArrowLeft className="w-4 h-4" /> Retour au blog
          </Link>
        </div>
      </article>
    </div>
  );
}

export { BlogList, BlogArticle };
